import { Component, Input, EventEmitter, Output, OnChanges } from '@angular/core';
import { CustomAnswer, CustomQuestion } from 'globals/store/events.data';
import { JoiFile } from 'globals/store/files.model';
import { Dictionary } from '@ngrx/entity';
import { MAX_ENTITY_STARS } from 'globals/helpers/constants';
import { StarsComponent } from '../misc/stars.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GetNumberPipe } from 'src/app/services/pipes/prettyNumbers.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { InputEditorComponent } from '../joi-input/input-editor';
import { FileHandlerComponent } from '../file-handler/file-handler.component';

@Component({
    selector: 'custom-questions',
    template: `
<ng-container *ngFor="let question of questions">
  <ng-container *ngIf="entity && entity.customQuestions && entity.customQuestions[question.id] !== 'undefined'">
    <div class="cq-holder" [ngClass]="{ 'alert-required': showMandatoryWarnings && question.isMandatory && !entity.customQuestions[question.id] , oneLine: question.type !== 'free' && question.type !== 'link' }">
      <div *ngIf="question.type === 'text'" [innerHTML]="question.question"></div>
      <div *ngIf="question.type !== 'checkbox' && question.type !== 'text'" [class]="question.isMandatory ? 'prompt with-mandatory' : 'prompt'"><em *ngIf="question.isMandatory">* </em><div [innerHTML]="question.question || 'Question without text: '"></div></div>
      <file-handler *ngIf="question.type === 'link'"
        [data]="{ files: entity.customQuestions[question.id] || [] }"
        [namePlaceholder]="'Link'"
        [noFilesMessage]="'No link provided'"
        [addText]="'Add link'"
        [fileLimit]="1"
        [hideByline]="true"
        (dataChange)="saveFiles($event, question)"
      ></file-handler>
      <input-editor *ngIf="question.type === 'free'"
        [object]="entity.customQuestions"
        [prop]="question.id"
        (changed)="update(question)"
      ></input-editor>
      <mat-radio-group *ngIf="question && question.id && question.type === 'radio'" [name]="question.id"
        [value]="entity.customQuestions[question.id]"
        [(ngModel)]="entity.customQuestions[question.id]">
        <mat-radio-button *ngFor="let type of yesNoOptions"
          [value]="type.id"  
          (click)="type.id === oldAnswers[question.id] ? updateRadio(question, null) : updateRadio(question, type.id)">
          {{ type.label }}
        </mat-radio-button>
      </mat-radio-group>
      <mat-checkbox *ngIf="question.type === 'checkbox'" color="primary"
        [(ngModel)]="entity.customQuestions[question.id]"
        (change)="update(question)"
      ></mat-checkbox>
      <stars *ngIf="question.type === 'stars'" 
        style="margin: 0 0 0 10px;"
        [maxStars]="MAX_ENTITY_STARS" 
        [stars]="entity.customQuestions[question.id] | getNumber" 
        (setStars)="saveStars($event, question)"
      ></stars>
      <div *ngIf="question.type === 'checkbox'" class="prompt-right" [innerHTML]="(question.isMandatory ? '* ' : '')+(question.question || 'Question without text: ')"></div>
    </div>
  </ng-container>
  
</ng-container>
`,
    styleUrls: ['custom-questions.scss'],
    standalone: true,
    imports: [StarsComponent, MatCheckboxModule, GetNumberPipe, CommonModule, FormsModule, MatRadioModule, InputEditorComponent, FileHandlerComponent],
})
export class CustomQuestionsComponent<E extends { customQuestions?: Dictionary<CustomAnswer>, id?: string }> implements OnChanges {
  @Input() entity: E
  @Input() questions: CustomQuestion[] = []
  @Input() shareView: boolean
  @Input() showMandatoryWarnings: boolean
  @Output() updateQuestion: EventEmitter<UpdateQuestionOutput> = new EventEmitter()
  oldAnswers: { [key: string]: CustomAnswer } = {}
  MAX_ENTITY_STARS = MAX_ENTITY_STARS

  yesNoOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No'}
  ]
  settingNull = false

  ngOnChanges() {
    if (!this.entity || !this.questions) return
    this.questions.filter(({ id }) => !this.entity.customQuestions[id]).forEach(({ id, type, answerDefault }) => {
      switch (type) { // mutates entity directly.  A little messy but need object / model for at least the input editor
        case 'free':
          this.entity.customQuestions[id] = answerDefault || ''
          break
        case 'checkbox':
          this.entity.customQuestions[id] = answerDefault || false
          break
        case 'radio':
          this.entity.customQuestions[id] = answerDefault || null
          break
      }
    })
    this.questions.forEach(({ id }) => this.oldAnswers[id] = this.entity.customQuestions[id])
  }

  saveFiles(files: JoiFile[], { id }: CustomQuestion) {
    this.updateQuestion.emit({ id, answer: files })
  }

  saveStars(stars: number, { id }: CustomQuestion) {
    this.entity.customQuestions[id] = stars
    this.updateQuestion.emit({ id, answer: stars })
  }

  updateCheck({ id }: CustomQuestion) { 
    this.updateQuestion.emit({ id, answer: this.oldAnswers[id] })
  }

  update({ id }: CustomQuestion) {
    console.log("Update " + id, "Setting null: " + this.settingNull)
    if (this.settingNull) return
    this.updateQuestion.emit({ id, answer: this.entity.customQuestions[id] })
  }

  updateRadio({ id }: CustomQuestion, answer: CustomAnswer) {
    this.updateQuestion.emit({ id, answer })
  }

}

export interface UpdateQuestionOutput {
  id: string
  answer: CustomAnswer
}

